
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useOnboarding } from "../OnboardingProvider";
import { useOnboardingProgress } from "@/hooks/useOnboardingProgress";
import { useToast } from "@/hooks/use-toast";

interface WelcomeStepProps {
  onPrev: () => void;
}

export function WelcomeStep({ onPrev }: WelcomeStepProps) {
  const navigate = useNavigate();
  const { completeStep, closeOnboarding } = useOnboarding();
  const { completeOnboarding } = useOnboardingProgress();
  const { toast } = useToast();
  const [isFinishing, setIsFinishing] = useState(false);

  const handleFinish = async () => {
    setIsFinishing(true);

    try {
      // Marcar step final como completo
      completeStep('welcome');
      
      // Marcar onboarding como completamente finalizado
      await completeOnboarding();

      toast({
        title: "Onboarding concluído!",
        description: "Bem-vindo à plataforma! Agora vamos configurar suas integrações.",
      });

      // Fechar modal e redirecionar
      closeOnboarding();
      navigate('/integrations');
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível finalizar o onboarding",
        variant: "destructive",
      });
    } finally {
      setIsFinishing(false);
    }
  };

  const completedItems = [
    "✅ Perfil completado com sucesso",
    "✅ Sistema de cashback configurado",
    "✅ Termos de uso aceitos",
    "✅ Loja integrada com sucesso",
    "✅ Conta pronta para uso",
  ];

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-xl font-semibold mb-2">Parabéns! 🎉</h2>
        <p className="text-muted-foreground">
          Sua conta foi configurada com sucesso e está pronta para uso.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Configuração Concluída</CardTitle>
          <CardDescription>
            Tudo foi configurado corretamente
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {completedItems.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-primary">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <h3 className="font-medium">Próximo passo</h3>
              <p className="text-sm text-muted-foreground">
                Configure suas integrações para começar a receber pedidos e processar cashback automaticamente.
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-primary" />
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button variant="outline" onClick={onPrev} className="flex-1">
          Voltar
        </Button>
        <Button 
          onClick={handleFinish}
          disabled={isFinishing}
          className="flex-1"
        >
          {isFinishing ? "Finalizando..." : "Ir para Integrações"}
        </Button>
      </div>
    </div>
  );
}
